import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import {
  ArrowLeft, Target, Download, CheckCircle2, Info, AlertCircle,
  ExternalLink, Calendar,
} from "lucide-react";
import type { RadarEvaluation } from "@shared/schema";

type SavedEvaluation = RadarEvaluation & {
  radarTitle: string;
  radarSlug: string;
  productName: string | null;
  companyName: string | null;
};

function OutcomeChip({ outcome }: { outcome: string | null }) {
  if (outcome === "match") return (
    <Badge variant="outline" className="border-green-300 text-green-700 dark:text-green-400 gap-1">
      <CheckCircle2 className="h-3 w-3" />Well Aligned
    </Badge>
  );
  if (outcome === "near_threshold") return (
    <Badge variant="outline" className="border-blue-300 text-blue-700 dark:text-blue-400 gap-1">
      <Info className="h-3 w-3" />Near Threshold
    </Badge>
  );
  return (
    <Badge variant="outline" className="border-slate-300 text-slate-600 dark:text-slate-400 gap-1">
      <AlertCircle className="h-3 w-3" />Mismatched
    </Badge>
  );
}

function scoreColor(outcome: string | null) {
  if (outcome === "match") return "text-green-600 dark:text-green-400";
  if (outcome === "near_threshold") return "text-blue-600 dark:text-blue-400";
  return "text-slate-500 dark:text-slate-400";
}

export default function AccountEvaluationsPage() {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation("/login");
    }
  }, [authLoading, isAuthenticated, setLocation]);

  const { data: evaluations, isLoading } = useQuery<SavedEvaluation[]>({
    queryKey: ["/api/account/evaluations"],
    enabled: isAuthenticated,
  });

  if (authLoading || !isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 container mx-auto max-w-4xl py-8 px-4 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="flex-1 container mx-auto max-w-4xl py-8 px-4">
        <Link href="/account" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          My Account
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Radar Evaluations</h1>
            <p className="text-sm text-muted-foreground">Your saved supplier evaluations</p>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {isLoading ? (
            <>
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-32 w-full rounded-xl" />
            </>
          ) : !evaluations || evaluations.length === 0 ? (
            <Card>
              <CardContent className="pt-12 pb-12 flex flex-col items-center justify-center text-center">
                <Target className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No saved evaluations yet</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  Complete a Radar evaluation and save it to your account to find it here.
                </p>
                <Button asChild>
                  <Link href="/radar">
                    <Target className="mr-2 h-4 w-4" />
                    Browse Radars
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            evaluations.map((ev) => {
              const completedDate = ev.completedAt
                ? new Date(ev.completedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                : null;
              const modifiedDate = ev.lastModifiedAt
                ? new Date(ev.lastModifiedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                : null;
              const score = Math.round(ev.overallScore ?? 0);

              return (
                <Card key={ev.id} className="hover:border-primary/30 transition-colors" data-testid={`card-evaluation-${ev.id}`}>
                  <CardContent className="pt-5 pb-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-base truncate">{ev.radarTitle}</h3>
                          <OutcomeChip outcome={ev.matchOutcome} />
                        </div>
                        {ev.productName && (
                          <p className="text-sm text-muted-foreground">{ev.productName}</p>
                        )}
                        {ev.companyName && (
                          <p className="text-xs text-muted-foreground">{ev.companyName}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                          {completedDate && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Completed {completedDate}
                            </span>
                          )}
                          {modifiedDate && (
                            <span className="flex items-center gap-1 text-primary">
                              Modified {modifiedDate}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex sm:flex-col items-center sm:items-end gap-3 shrink-0">
                        <div className="text-right">
                          <span className={`text-3xl font-extrabold leading-none ${scoreColor(ev.matchOutcome)}`}>
                            {score}
                          </span>
                          <span className="text-sm text-muted-foreground ml-1">/ 100</span>
                        </div>

                        <div className="flex gap-2 flex-wrap sm:justify-end">
                          <Button
                            asChild
                            size="sm"
                            variant="outline"
                            data-testid={`button-view-evaluation-${ev.id}`}
                          >
                            <Link href={`/radar/${ev.radarSlug}/results/${ev.id}`}>
                              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                              View Results
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/api/radar/evaluations/${ev.id}/pdf`, "_blank")}
                            data-testid={`button-download-evaluation-${ev.id}`}
                          >
                            <Download className="mr-1.5 h-3.5 w-3.5" />
                            PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
